<template>
  <!-- Modal pour la signature du document -->
  <b-modal 
    @hide="OnCancel()"
    v-model="isVisible" 
    size="xl" 
    centered 
    hide-footer 
    no-close-on-backdrop 
    no-close-on-esc
    :title="modalTitle">
    <!-- TABULATION POUR LA VALIDATION DES TERMES ET CONDITIONS -->
    <b-tabs v-model="stepIndex" nav-class="d-none">
      <b-tab title="Document" active>
        <b-col style="height: 60vh; overflow-y: auto;" class="border rounded mb-3">
            <vue-pdf-embed v-if="SignedPdf" :source="SignedPdf" />
        </b-col>
        <b-col class="d-flex justify-content-between align-items-center bg-light p-2 rounded">
          <b-form-checkbox v-model="termsAccepted">
            {{ $t('TermsAndUse') }}
          </b-form-checkbox>
          <!-- BOUTON POUR PASSAGE ETAPE SIGNATURE -->
          <b-button 
            @click="goToSignatureStep" 
            :disabled="!termsAccepted && enableSignDocument"
            variant="primary" 
            pill>
            {{ $t('SignDocument') }}
          </b-button>
        </b-col>
      </b-tab>
      <!-- TABULATION SIGNATURE -->
      <b-tab title="Signature">
        <b-col class="text-center mb-3">
          <h5>{{ $t('ChooseYourSignatureMethod') }}</h5>
        </b-col>
        <b-card no-body>
          <b-tabs v-model="signatureTabIndex" pills card justified @input="onSignatureTypeChange">
            <b-tab :title="$t('Draw')" active>
              <b-col class="d-flex justify-content-center align-items-center p-3 bg-light">
                <!-- COMPOSANT POUR LA SIGNATURE MANUELLE -->
                <VueSignaturePad
                  width="400px"
                  height="200px"
                  ref="signaturePad"
                  :options="{ penColor: '#030202' }"
                  class="border bg-white shadow-sm"/>
              </b-col>
            </b-tab>
            <b-tab :title="$t('ImportAnImage').toString()">
              <b-col class="p-3">
                <!-- FORMULAIRE D'IMPORATION D'IMAGE -->
                <b-form-file
                  v-model="uploadedFile"
                  :placeholder="$t('ChooseImagePngJpg').toString()"
                  :drop-placeholder="$t('DropImageHere').toString()"
                  accept="image/jpeg, image/png"
                  @change="handleFileUpload"/>
                <b-col v-if="uploadedSignatureBase64" class="mt-3 text-center border bg-white p-2" style="height: 200px;">
                  <!-- AFFICHAGE DE L'IMAGE IMPORTÉE -->
                  <img :src="uploadedSignatureBase64" style="max-width: 100%; max-height: 100%; object-fit: contain;"/>
                </b-col>
                <b-col v-else class="mt-3 text-center text-muted border bg-light p-2" style="height: 200px; line-height: 200px;">
                  {{ $t('NoImageSelected') }}
                </b-col>
              </b-col>
            </b-tab>
          </b-tabs>
          <b-row v-if="!Certificate" class="m-2">
            <b-col md="6">
              <b-form-group :label="$t('LastName') + ' :'" label-for="input-lastname">
                <b-form-input
                  id="input-lastname"
                  v-model="lastname"
                  type="text"
                  :placeholder="$t('EnterLastName')"
                  required
                ></b-form-input>
              </b-form-group>
            </b-col>
            <b-col md="6">
              <b-form-group :label="$t('FirstName') + ' :'" label-for="input-firstname">
                <b-form-input
                  id="input-firstname"
                  v-model="firstname"
                  type="text"
                  :placeholder="$t('EnterFirstName')"
                  required/>
              </b-form-group>
            </b-col>
          </b-row>
        </b-card>
        <b-col class="d-flex justify-content-between align-items-center mt-3">
            <!-- BOUTON PASSAGE ETAPE PRECEDANTE --> 
          <b-button @click="stepIndex--" variant="outline-secondary" pill>
            {{ $t('Back') }}
          </b-button>
          <b-col class="d-flex justify-content-end">
            <!-- BOUTON EFFACER SIGNATURE -->
            <b-button v-if="signatureTabIndex === 0" @click="undoSignature" pill variant="outline-danger" class="mr-2">
              {{ $t('Clear') }}
            </b-button>
            <!-- BOUTON PASSAGE ETAPE SUIVANTE --> 
            <b-button @click="goToPasswordStep" variant="primary" pill :disabled="isSending">
              <b-spinner v-if="isSending" small class="mr-1"></b-spinner>
              {{ strategy && strategy.requiresPasswordValidation ? $t('Next') : $t('Confirm') }}
            </b-button>
          </b-col>
        </b-col>
      </b-tab>
      <!-- TABULATION VALIDATION MOT DE PASSE -->
      <b-tab :title="$t('Validation')">
        <PasswordValidation
          @Submit="saveCustomerSignature"
          @Back="stepIndex--"/>
      </b-tab>
    </b-tabs>
  </b-modal>
</template>

<script lang="ts">
  /**
   * Composants vue js
   */
  import { Component } from 'vue-property-decorator';

  /**
   * Composant pour l'affichage du pdf
   */
  import VuePdfEmbed from 'vue-pdf-embed/dist/vue2-pdf-embed';
  import { PDFDocument, StandardFonts, rgb } from 'pdf-lib';

  /**
   * Moment pour la gestion des dates
   */
  import moment from 'moment';
  
  /**
   * Traductions
   */
  import locale from '../../locales/SMA.json';

  /**
   * Classes et énumérations
   */
  import { ParticularCustomerInformation } from '@/types/Administration/SMA/CustomerInfo';

  /**
   * Composants Vee Validate pour valider les formulaires
   */
  import { ValidationProvider } from 'vee-validate';
  import '@/vee-rules';
  
  /**
   * Classe commune à la validation des mandats
   */
  import { CertificateValidation } from './CertificateValidation';

  /**
   * Composant de validation mot de passe
   */
  import PasswordValidation from './PasswordValidation.vue';
  
  @Component({
    components: { VuePdfEmbed, ValidationProvider, PasswordValidation },
    i18n: { messages: locale }
  })
  /**
   * Composant pour la signature du pdf 
   * Signature faite à partir d'un dessin sur l'interface ou importé par image
   * Validation final par l'entré du mot de passe du compte
   */
  export default class SigningUpCertificateCustomer extends CertificateValidation {
    
    // --- Gestion de la modale et des étapes ---
    
    private signatureTabIndex: number = 0;                                    // 0: Dessin, 1: Importation

    // --- Données ---
    private termsAccepted: boolean = false;                                   // Validation des termes d'utilisation

    private lastname: string = "";
    private firstname: string = "";

    private when: string = moment().format('DD MMMM YYYY').toString();        // Date de signature pour affichage dans le pdf

    $refs!: {
        signaturePad: any
    };

    // Récupération du titre de la modal
    get modalTitle() {
        switch(this.stepIndex) {
            case 0: return this.$t('DocumentReview');
            case 1: return this.$t('SignDocument');
            case 2: return this.$t('Validation');
            default: return "";
        }
    }

    // --- Navigation entre étapes ---

    /**
     * => Envoi sur la partie signature
     */
    private goToSignatureStep() {
        this.stepIndex = 1;
        
        this.$nextTick(() => {
           setTimeout(() => {
                if(this.signatureTabIndex === 0) this.resizePad();
           }, 200);
        });
    }

    /**
     * => Envoi sur la partie validation mot de passe
     * Renvoi du message d'erreur si aucune signature est présente
     */
    private async goToPasswordStep() {
      if(!this.Certificate && (this.firstname == "" && this.lastname == "")) {
        this.showToast(this.$t('PleaseEnterYourName').toString(), "warning", 'Error');
        return;
      }

      if (this.signatureTabIndex === 0) { // Draw
          const { isEmpty } = this.$refs.signaturePad.saveSignature();
          if (isEmpty) {
            this.showToast(this.$t('PleaseSignTheDrawingArea').toString(), "warning", 'Error');
            return;
          }
      } else {
          if (!this.uploadedSignatureBase64) {
            this.showToast(this.$t('PleaseUploadSignatureImage').toString(), "warning", 'Error');
            return;
          }
      }

      let finalSignatureData: string | null = null;

      if (this.signatureTabIndex === 0) {
        const { data } = this.$refs.signaturePad.saveSignature();
        finalSignatureData = data;
      } else {
        finalSignatureData = this.uploadedSignatureBase64;
      }

      this.signedPdfBlob[0] = await this.embedSignatureClientSide(finalSignatureData!);

      if (this.strategy.requiresPasswordValidation){
        this.stepIndex++;
      }
      else{
        this.saveCustomerSignature("");
      }
    }

    // --- Logique Signature ---

    /**
     * Changement du type de signature et donc changement de b-tabs
     * @param index Index
     */
    private onSignatureTypeChange(index: number) {
        if (index === 0) {
            this.$nextTick(() => this.resizePad());
        }
    }

    /**
     * Resize du pad 
     */
    private resizePad() {
        const pad = this.$refs.signaturePad as any;
        if (pad && pad.resizeCanvas) {
            pad.resizeCanvas();
        }
    }

    /**
     * Suppression de la signature faite sur le pad
     */
    private undoSignature() {
      (this.$refs.signaturePad as any).undoSignature();
    }

    /**
     * Méthode pour l'importation d'image de la signature
     * @param event Event d'importation
     */
    private handleFileUpload(event: any) {
      const file = event.target.files[0];
      if (!file) return;

      if (file.type !== 'image/png' && file.type !== 'image/jpeg') {
          this.showToast(this.$t('InvalidFormatUsePngOrJpg').toString(), "danger", 'Error');
          return;
      }

      const reader = new FileReader();
      reader.onload = (e: any) => {
        this.uploadedSignatureBase64 = e.target.result;
      };
      reader.readAsDataURL(file);
    }

    /**
     * Intégration de la signature dessiné ou importé sur le pdf.
     * Intégration toujours aux même coordonnées.
     * @param signatureBase64 Signature en base64
     */
    private async embedSignatureClientSide(signatureBase64: string): Promise<Blob> {
      try {
        const existingPdfBytes = await this.Source.arrayBuffer();
        const pdfDoc = await PDFDocument.load(existingPdfBytes);
        const font = await pdfDoc.embedFont(StandardFonts.Helvetica);

        const signatureImage = await pdfDoc.embedPng(signatureBase64);

        const pages = pdfDoc.getPages();
        const lastPage = pages[pages.length - 1];
        const { width, height } = lastPage.getSize();

        lastPage.drawImage(signatureImage, {
          x: width - (signatureImage.width / 2) - 100,
          y: height - (signatureImage.height / 2) - 140,
          width: signatureImage.width / 2,
          height: signatureImage.height / 2,
        });
        
        const info = this.Certificate?.CustomerInfo as ParticularCustomerInformation;

        if(info != null){
          lastPage.drawText(info?.CustomerCity || "", {
            x: 50, y: height - 135, size: 12, font, color: rgb(0, 0, 0),
          });
        }

        const fullName = `${info?.LastName ?? this.lastname} ${info?.FirstName ?? this.firstname}`;
        
        lastPage.drawText(fullName, {
          x: 350, y: height - 135, size: 12, font, color: rgb(0, 0, 0),
        });

        lastPage.drawText(this.when, {
          x: 50, y: height - 165, size: 10, font, color: rgb(0, 0, 0),
        });

        const pdfBytes = await pdfDoc.save();
        return new Blob([pdfBytes], { type: 'application/pdf' });

      } catch (e) {
        console.error("Erreur PDF Lib", e);
        throw e;
      }
    }

    /**
     * Lors de l'annulation de la signature
     */
    private OnCancel(){
      this.$emit("cancel");
    }

    /**
     * Lors du montage du composant, création et téléchargement du pdf 
     */
    private async mounted(){
      this.init();
    }

    /**
     * Avant destruction du composant
     * Révocation de l'URL du PDF
     */
    private beforeDestroy() {
      if (this.SignedPdf) URL.revokeObjectURL(this.SignedPdf);
    }
  }
</script>

<style scoped>
/* Optionnel : pour cacher les onglets si le prop no-nav-style ne suffit pas sur votre version bootstrap-vue */
::v-deep .nav-tabs {
    display: none !important; 
}
</style>